// src/pages/Historico/useExperimentGroups.jsx
import { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { getExperimentos } from '../../infra/api/ExperimentoApi';

dayjs.locale('pt-br');

// Agrupa sempre por startDate — o sortField controla apenas a ordenação dos grupos,
// nunca o critério de agrupamento. Experimentos com datas de início diferentes não
// devem cair no mesmo grupo só porque encerraram no mesmo dia.
function groupEnrichedItems(enrichedItems, sortField = 'startDate') {
    if (!enrichedItems) return [];

    const byDay = {};
    enrichedItems.forEach((exp) => {
        // Agrupa SEMPRE pelo início do experimento
        const effectiveTime = exp.startDate;

        const d = dayjs.unix(effectiveTime).startOf('day').format('YYYY-MM-DD');
        (byDay[d] = byDay[d] || []).push(exp);
    });

    const dateGroups = Object.keys(byDay)
        .map((date) => ({ date, items: byDay[date] }))
        // Quando sort === 'endDate', ordena os grupos pelo endDate mais recente do grupo;
        // caso contrário, ordena simplesmente pela data de início (mais recente primeiro).
        .sort((a, b) => {
            if (sortField === 'endDate') {
                const latestA = Math.max(...a.items.map((e) => e.endDate || e.startDate));
                const latestB = Math.max(...b.items.map((e) => e.endDate || e.startDate));
                return latestB - latestA;
            }
            return a.date < b.date ? 1 : -1;
        });

    const final = dateGroups.map(({ date, items: its }) => {
        const byProto = {};
        its.forEach((exp) => {
            const protoName = exp.protocolName;
            (byProto[protoName] = byProto[protoName] || []).push(exp);
        });

        const protos = Object.keys(byProto)
            .sort((a, b) => a.localeCompare(b, 'pt', { sensitivity: 'base' }))
            .map((protoName) => {
                const protoItems = byProto[protoName];

                const byAnalysis = {};
                protoItems.forEach((item) => {
                    const analysisName = item.analysisName;
                    if (!byAnalysis[analysisName]) {
                        byAnalysis[analysisName] = [];
                    }
                    byAnalysis[analysisName].push(item);
                });

                const analyses = Object.keys(byAnalysis).map(
                    (analysisName) => ({
                        analysisName,
                        items: byAnalysis[analysisName],
                    })
                );

                return { protoName, protoColor: protoItems[0]?.protocolColor || null, analyses };
            });

        return { date, protos };
    });

    return final.filter((g) => g.protos.length > 0);
}

export function useExperimentGroups(query, protocols = []) {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageInfo, setPageInfo] = useState({
        page: 0,
        size: 20,
        totalPages: 0,
        totalElements: 0,
        itemsInPage: 0,
    });
    const [refreshKey, setRefreshKey] = useState(0);

    const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

    useEffect(() => {
        let alive = true;
        if (!protocols || protocols.length === 0) {
            setLoading(true);
            return;
        }

        (async () => {
            setLoading(true);
            try {
                const pageRes = await getExperimentos(query, false);

                if (!alive) return;

                const enrichedItems = (pageRes?.items || []).map((exp) => {
                    const protocol = protocols.find(
                        (p) => p.id === exp.protocolId
                    );

                    let analysis = null;
                    if (protocol?.analysis?.length > 0) {
                        if (exp.analysisId) {
                            analysis = protocol.analysis.find(
                                (a) => a.id === exp.analysisId
                            );
                        }
                        if (!analysis) {
                            analysis = protocol.analysis[0];
                        }
                    }

                    return {
                        ...exp,
                        protocolName: protocol?.name || exp.protocolId,
                        analysisName: analysis?.name || 'Análise Padrão',
                        protocolColor: protocol?.color,
                        analysisData: analysis,
                    };
                });

                // ✅ FIX: Passamos query.sort para a função saber se agrupa por inicio ou fim
                const groupedData = groupEnrichedItems(
                    enrichedItems,
                    query.sort
                );
                setGroups(groupedData);

                setPageInfo({
                    page: pageRes?.page ?? 0,
                    size: pageRes?.size ?? 20,
                    totalPages: pageRes?.totalPages ?? 0,
                    totalElements: pageRes?.totalElements ?? 0,
                    itemsInPage: (pageRes?.items || []).length,
                });
            } catch (error) {
                console.error('Falha ao buscar experimentos:', error);
                if (!alive) return;
                setGroups([]);
                setPageInfo({
                    page: 0,
                    size: 20,
                    totalPages: 0,
                    totalElements: 0,
                    itemsInPage: 0,
                });
            } finally {
                if (alive) {
                    setLoading(false);
                }
            }
        })();

        return () => (alive = false);
    }, [query, protocols, refreshKey]);

    return { groups, loading, pageInfo, refetch };
}
